<template>
<div
    v-if="templates != null"
>
    <v-text-field
        label="Search"
        prepend-icon="mdi-magnify"
        v-model="full_text"
        autocomplete="off"
        v-if="show_icon_bar"
    >
        <v-menu
            bottom
            offset-y
            slot="append-outer"
        >
            <template v-slot:activator="{ on }">
                <v-icon
                    v-on="on"
                >
                    mdi-dots-vertical
                </v-icon>
            </template>
    
            <v-list>
                <v-list-item
                    v-on:click="onClickAdd()"
                     v-if="uncreated_templates != null && uncreated_templates.length > 0"
                >
                    <v-list-item-title>New...</v-list-item-title>
                </v-list-item>
                <m-restricted-menu-list-item
                    v-if="can_view_all_message_table"
                    text="All Automated Message Types..."
                    @click="onClickShowAllMessageTable"
                />
            </v-list>
        </v-menu> 
    </v-text-field>

    <v-data-table
        v-if="templates != null"
        :headers="headers"
        :items="templates"
        :items-per-page="100"
        class="elevation-1"
        :hide-default-footer="true"
        disable-sort
        :footer-props="{
            disableItemsPerPage: false,
            disablePagination: false,
        }"
        :search="full_text"
    >
    
        <template v-slot:item.send_from="{ item }">
            <span :class="item.send_from == null && (!item.conditions?.length || !item.conditions.every(({send_from}) => !!send_from)) ? 'error--text font-weight-bold' : ''">
                {{ pretty_send_from(item) }}
            </span>
        </template>

        <template v-slot:item.comp_best_send_mode="{ item }">
            <v-chip
                :color="getModeChipColor(item.comp_best_send_mode)"
                text-color="white"
                small
            >
                <v-icon
                    left
                    small
                >
                    {{ getModeIcon(item.comp_best_send_mode) }}
                </v-icon>
                {{ item.comp_best_send_mode.charAt(0).toUpperCase() + item.comp_best_send_mode.slice(1).toLowerCase() }}
            </v-chip>
        </template>

        <template v-slot:item.can_send_status="{ item }">
            <v-menu
                v-if="hasIssues(item)"
                v-model="item.notes_menu_open"
                bottom
                offset-y
                close-on-click
                close-on-content-click
                max-width="400px"
            >
                <template v-slot:activator="{ on, attrs }">
                    <v-chip
                        v-bind="attrs"
                        v-on="on"
                        :color="getCanSendChipColor(item)"
                        :text-color="getCanSendChipTextColor(item)"
                        small
                        style="cursor: pointer;"
                    >
                        <v-icon
                            left
                            small
                        >
                            {{ getCanSendIcon(item) }}
                        </v-icon>
                        {{ item.notes_count }} {{ item.notes_count === 1 ? 'Note' : 'Notes' }}
                    </v-chip>
                </template>

                <v-card>
                    <v-card-title class="text-subtitle-1 pb-2">
                        <v-icon left color="info">mdi-information</v-icon>
                        Send Notes
                    </v-card-title>

                    <v-card-text class="pb-2">
                        <ul class="pl-4 my-0">
                            <li
                                v-for="(note, index) in getAllNotes(item)"
                                :key="index"
                                class="mb-1"
                            >
                                {{ note }}
                            </li>
                        </ul>
                    </v-card-text>
                </v-card>
            </v-menu>
        </template>

        <template v-slot:item.list_drip_delay="{ item }">
            {{$rc.pretty(item.list_drip_delay,'duration')}}
        </template>

        

        <template v-slot:item.message_patterns="{ item }">
            <ul
                v-if="item.message_patterns.length > 1"
            >
                <li
                    v-for="(message, index) in item.message_patterns"
                    :key="item.template_subject_id+'_'+index"
                >
                    {{message}}
                </li>
            </ul>
            <span
                v-else
            >   
                {{item.message_patterns[0]}}
            </span>
        </template>

        

        <template v-slot:item.action="{ item }">
            <v-menu 
                bottom 
                offset-y  
            >
            
            <template v-slot:activator="{ on }">
                    
                <v-icon 
                    v-on="on"
                >
                    mdi-dots-vertical-circle-outline
                </v-icon>
                    
            </template>
            
            <v-list>
                <v-list-item v-on:click="onClickTemplateSettings(item)">
                    <v-list-item-title>Settings...</v-list-item-title>
                </v-list-item>

                <v-list-item v-on:click="onClickMessages(item)">
                    <v-list-item-title>Messages...</v-list-item-title>
                </v-list-item>

                <v-divider/>

                <v-list-item v-on:click="onClickDelete(item)">    
                    <v-list-item-title>Delete...</v-list-item-title>
                </v-list-item>    

                <v-divider
                    v-if="item.upgrade_available_to != null"
                />

            </v-list>
            
            </v-menu> 
        </template>
    
    </v-data-table>

    <m-icon-bar
        v-on:add="onClickAdd"
        v-if="uncreated_templates != null && uncreated_templates.length > 0 && show_icon_bar"
    />

    <v-alert
        v-if="any_messages_in_manual_mode && show_manual_warning"
        type="warning"
        color="warning"
        class="mt-6"
        icon="mdi-information"
    >
        You have message(s) that are <strong>MANUAL</strong> mode. These messages will not be sent unless you manually review and approve them.
    </v-alert>

    <m-auto-message-create-new-dialog
        ref="create-new-dialog"
        :organization_id="organization_id"
        v-on:confirm="onConfirmNewTemplate"
    />

    <m-new-drip-message-dialog
        ref="new-drip-message-dialog"
        v-on:ok="update"
    />

    <m-auto-message-template-restrictions-dialog
        ref="template-restrictions-dialog"
    />


</div>
</template>

<script>
import AutoMessageDisplayHelpers from '@/mixins/auto-messages/summary-display-helpers.js';
import MAutoMessageCreateNewDialog from '@/views/auto-messages/auto-message-create-new-dialog.vue';
import MAutoMessageTemplateRestrictionsDialog from '@/views/auto-messages/auto-message-template-restrictions-dialog.vue';
import MRestrictedMenuListItem from '@/components/global/m-restricted-menu-list-item.vue';

export default {
    mixins: [AutoMessageDisplayHelpers],

	components: {
		MAutoMessageCreateNewDialog,
		MAutoMessageTemplateRestrictionsDialog,
		MRestrictedMenuListItem,
	},

    props: {

        organization_id: {
            type: String,
            default: function() { return this.$route.params.organization_id; }
        },

        show_column_type: {
            type: Boolean,
            default: function() { return true; }
        },

        show_column_patterns: {
            type: Boolean,
            default: function() { return false; }
        },

        show_column_delay: {
            type: Boolean,
            default: function() { return false; }
        },

        list_id: {
            type: String,
            default: function() { return null; }
        },

        show_list_label: {
            type: Boolean,
            default: function() { return true; }
        },

        show_column_params: {
            type: Boolean,
            default: function() { return true; }
        },

        show_column_notes: {
            type: Boolean,
            default: function() { return true; }
        },

        show_manual_warning: {
            type: Boolean,
            default: function() { return true; }
        },

        show_icon_bar: {
            type: Boolean,
            default: function() { return true; }
        },

        show_auto_only: {
            type: Boolean,
            default: function() { return false; }
        },
    },

	data() {
		return{
			organization_label: null,
			templates: null,
			uncreated_templates: null,
			full_text: null,
			all_template_contexts: [],
			all_template_contexts_loaded: false,
			loading_template_contexts: false,
		}
	},

    mounted () {
        this.update();
    },

    // Navigation guards to ensure data refreshes when navigating to this component
    beforeRouteEnter(_to, _from, next) {
        next(vm => {
            // Force refresh when entering this route
            vm.update();
        });
    },

    beforeRouteUpdate(_to, _from, next) {
        // Force refresh when route params change
        this.update();
        next();
    },

    // For keep-alive scenarios, refresh data when component becomes active
    activated() {
        this.update();
    },

    // Watch for route changes to refresh data when query parameters change
    watch: {
        '$route'(to, from) {
            // If the route path is the same but query params changed (like our _refresh param),
            // refresh the data
            if (to.path === from.path && to.query._refresh !== from.query._refresh) {
                this.update();
            }
        }
    },

    methods:
    {
        update()
        {
            this.$rc.showSpinner();

            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PUT: /api/v1/auto-messages/org-profile',
            
                'params' : {
                        'organization_id': this.organization_id,
                        'list_id': this.list_id,
                    },
            
                'on_success': (data) => {
                        this.organization_label = data.organization_label;
                        
                        const templatesWithNotesCount = data.templates.map(template => ({
                            ...template,
                            notes_count: this.autoMessageGetAllNotes(template).length
                        }));

                        if ( this.show_auto_only )
                        {
                            this.templates = templatesWithNotesCount.filter(
                                (item) => {
                                    return item.comp_best_send_mode == 'auto';
                                }
                            );
                        }
                        else
                        {
                            this.templates = templatesWithNotesCount;
                        }

						this.uncreated_templates = data.uncreated_templates.sort((a, b) => 
							a.template_label.localeCompare(b.template_label)
						);

                        // Initialize birthday templates with default certification status to prevent loading delays
                        this.initializeBirthdayTemplateStatus();

                        // Load data cleaning certification status for birthday message templates
                        this.loadDataCleaningCertificationStatus();
                    },
            
                'on_error': 'Unable to get automated messages',
            });
        },

        onClickTemplateSettings(template)
        {
            this.$router.push({
                'name': 'auto-messages-template-settings',
                'params' : {
                    'organization_id' : template.organization_id,
                    'template_context' : template.template_context,
                    'template_subject_id': template.template_subject_id,
                }
            }).catch(e=>{}); // swallow any exception    
        },

        onClickDelete(template)
        {
            this.$rc.confirm(
                'Delete auto message template.',
                'Do you want to delete the template '+template.template_label+'.',
                [
                    {'text': 'Cancel', 'value': 'cancel'},
                    {'text': 'Confirm', 'value': 'confirm'},
                ],
                (response) => {
                    if (response == 'confirm') 
                        this.onDoDelete(template);
                }
            );
        },

        onDoDelete(template)
        {
            let params = {
                'organization_id' : template.organization_id,
                'template_context' : template.template_context,
                'template_subject_id': template.template_subject_id,
            };

            this.$rc.showSpinner();
            
            this.$rc.deleteAutoMessageTemplate(
                params,
            
                (data) => {
                    this.$rc.hideSpinner();

                    this.update();
                },
            
                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error','Unable to delete auto message template');
                },
            );
        },

		onClickAdd()
		{
			this.triggerAddWithPreselection(null, null);
		},

		onClickShowAllMessageTable()
		{
			if (!this.can_view_all_message_table)
			{
				return;
			}

			this.fetchAllTemplateContexts().then(() =>
			{
				if (!this.all_template_contexts.length)
				{
					this.$rc.alert('Info', 'No restriction metadata available.');
					return;
				}

				this.$refs['template-restrictions-dialog'].show(this.all_template_contexts);
			});
		},

		triggerAddWithPreselection(template_context, order_confirmation_settings_id = null) {
            if (this.list_id != null) {
                this.onClickAddDripMessage();
                return;
            }

            if (!this.uncreated_templates) {
                return;
            }

            let choices = [];
            let ut = this.uncreated_templates;
            let preselected_value = null;

            for ( let i = 0; i < ut.length; i++ ) {
                const choice = {
                    'text' : ut[i].template_label,
                    'value' : ut[i].template_context,
                    'template' : ut[i],
                    'restrictions': ut[i].template_restrictions || [],
                };

                choices.push(choice);

                if (template_context && ut[i].template_context === template_context) {
                    preselected_value = choice;
                }
            }

            if (preselected_value) {
                this.onCreateNewTemplate(preselected_value, true, order_confirmation_settings_id);
            } else {
                this.$refs['create-new-dialog'].showDialog(choices);
            }
        },

        onConfirmNewTemplate(selected_template_choice) {
            this.onCreateNewTemplate(selected_template_choice, false, null);
        },

        onClickAddDripMessage()
        {
            let send_from = null;

            if ( this.templates != null )
            {
                for ( let i = 0; i < this.templates.length; i++ )
                {
                    send_from = this.templates[i].send_from;
                    if ( send_from != null ) break;
                }
            }

            this.$refs['new-drip-message-dialog'].showDialog(this.organization_id, this.list_id, send_from);
        },

        onCreateNewTemplate(value, navigate_to_edit = false, order_confirmation_settings_id = null)
        {
            let new_template = value.template;

            this.$rc.showSpinner();
            
            this.$rc.putAutoMessageTemplate(
                new_template,
            
                (data) => {
                    if (navigate_to_edit) {
                        setTimeout(() => {
                            this.$rc.hideSpinner();
                            this.$router.push({
                                name: 'auto-messages-template-settings',
                                params: {
                                    organization_id: new_template.organization_id,
                                    template_context: new_template.template_context,
                                    template_subject_id: new_template.template_subject_id,
                                },
                                query: order_confirmation_settings_id ? {
                                    order_confirmation_settings_id: order_confirmation_settings_id,
                                } : {},
                            });
                        }, 1000); // avoid quick flash
                    } else {
                        this.$rc.hideSpinner();
                        this.update();
                    }
                },
            
                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error','Unable to create automated message');
                },
            );
        },

        onClickMessages(template)
        {
            this.$router.push({
                'name': 'auto-messages-search-messages',
                'params' : {
                    'organization_id' : template.organization_id,
                    'template_context' : template.template_context,
                    'template_subject_id': template.template_subject_id,
                }
            }).catch(e=>{}); // swallow any exception    
        },

        pretty_send_from(item) {
            return this.autoMessagePrettySendFrom(item);
        },

        hasIssues(item) {
            return this.autoMessageHasIssues(item);
        },

        getAllNotes(item) {
            return this.autoMessageGetAllNotes(item);
        },

        getCanSendChipColor(item) {
            // Always show info color for notes
            return "info";
        },

        getCanSendChipTextColor(item) {

            return "white";
        },

        getCanSendIcon(item) {
            // Always show information icon for notes
            return "mdi-information";
        },



        initializeBirthdayTemplateStatus() {
            // Initialize all birthday templates with default certification status to prevent loading delays
            if (!this.templates) return;

            this.templates.forEach(template => {
                if (template.template_context === 'birthday-message' ||
                    template.template_context === 'birthday-month-message') {
                    // Default to false until we load the actual status
                    template.data_cleaning_certified = false;
                }
            });
        },

		loadDataCleaningCertificationStatus() {
			// Check if we have any birthday message templates
			if (!this.templates) return;

            const has_birthday_templates = this.templates.some(template =>
                template.template_context === 'birthday-message' ||
                template.template_context === 'birthday-month-message'
            );

            if (!has_birthday_templates) return;

			// Load data cleaning certification status
			this.$rc.rest({
				'endpoint': 'GET: /api/v1/organization-data-cleaning-rules/:organization_id',
                'params': {
                    'organization_id': this.organization_id,
                },
                'on_success': (data) => {
                    //console.log('Data cleaning certification API response:', data);
                    // Update all birthday message templates with certification status
                    this.templates.forEach(template => {
                        if (template.template_context === 'birthday-message' ||
                            template.template_context === 'birthday-month-message') {
                            template.data_cleaning_certified = data.is_certified === true;
                            //console.log(`Updated template ${template.template_label} with certification status:`, template.data_cleaning_certified);
                        }
                    });
                    // Force reactivity update
                    this.$forceUpdate();
                },
                'on_error': () => {
                    //console.log('Data cleaning certification API error - setting all to false');
                    // If no rules exist yet, they're not certified
                    this.templates.forEach(template => {
                        if (template.template_context === 'birthday-message' ||
                            template.template_context === 'birthday-month-message') {
                            template.data_cleaning_certified = false;
                            //console.log(`Set template ${template.template_label} certification to false due to API error`);
                        }
                    });
                    // Force reactivity update
					this.$forceUpdate();
				}
			});
        },

		fetchAllTemplateContexts(force = false)
		{
			if (this.all_template_contexts_loaded && !force)
			{
				return Promise.resolve();
			}

			return new Promise((resolve) =>
			{
				this.loading_template_contexts = true;

				this.$rc.rest({
					'endpoint': 'GET: /api/v1/auto-messages/templates/all-contexts/',
					'on_success': (data) => {
						this.all_template_contexts = data.contexts || [];
						this.all_template_contexts_loaded = true;
						this.loading_template_contexts = false;
						resolve();
					},
					'on_error': () => {
						this.loading_template_contexts = false;
						this.$rc.alert('Error', 'Unable to load template restrictions');
						resolve();
					},
				});
			});
		},

        getModeChipColor(mode) {
            return this.autoMessageGetModeChipColor(mode);
        },

        getModeIcon(mode) {
            return this.autoMessageGetModeIcon(mode);
        }
    },

    computed: 
    {

        can_view_all_message_table() {
            return !!(this.$rc && typeof this.$rc.isAuthActorSystemAdmin === 'function' && this.$rc.isAuthActorSystemAdmin());
        },

        any_messages_in_manual_mode() {
            if ( this.templates == null ) return false;

            for ( let i = 0; i < this.templates.length; i++ )
            {
                if ( this.templates[i].comp_best_send_mode == 'manual' )
                    return true;
            }

            return false;
        },

        headers() {
            let ret = [];
            
            if ( this.show_column_type )
            {
                ret.push({ text: 'Type', value: 'template_context'});
            }

            if ( this.show_column_delay )
            {
                ret.push({ text: 'Delay', value: 'list_drip_delay'});
            }

            if ( this.show_list_label )
            {
                ret.push({ text: 'List', value: 'list_drip_list_internal_label'});
            }
               
            ret.push({ text: 'Label', value: 'template_label'});

            if ( this.show_column_params )
            {
                ret.push({ text: 'Parameters', value: 'param_description'});
            }

            if ( this.show_column_patterns )
            {
                ret.push({ text: 'Message', value: 'message_patterns'})
            }

            ret.push({ text: 'Send From', value: 'send_from', width: '150px'});
            ret.push({ text: 'Mode', value: 'comp_best_send_mode', align: 'center', width: '120px'});

            if ( this.show_column_notes )
            {
                ret.push({ text: 'Send Notes', value: 'can_send_status', align: 'center', width: '130px'});
            }

            ret.push({ text: 'Actions', value: 'action', align: 'right', width: '100px'});
            
            return ret;
        },
    },
}
</script>

<style lang="scss" scoped>

</style>
